import { useCallback, useState } from 'react';
import { Starfield } from './Starfield';
import { ProgressDots } from './zen-ui';
import { Intro } from './chapters/Intro';
import { KindleKey } from './chapters/KindleKey';
import { SignNote } from './chapters/SignNote';
import { ReleaseNote } from './chapters/ReleaseNote';
import { FadingRelay } from './chapters/FadingRelay';
import { Constellation } from './chapters/Constellation';
import { Farewell } from './chapters/Farewell';

const TOTAL_CHAPTERS = 7;

/**
 * Zen Relay — a quiet, minimal game that teaches the heart of Nostr:
 * keys, signatures, relays, resilience, and follows.
 */
export function ZenGame() {
  const [chapter, setChapter] = useState(0);

  const next = useCallback(
    () => setChapter((c) => Math.min(c + 1, TOTAL_CHAPTERS - 1)),
    [],
  );
  const restart = useCallback(() => setChapter(0), []);

  const inJourney = chapter > 0 && chapter < TOTAL_CHAPTERS - 1;

  return (
    <div className="dark relative min-h-dvh overflow-hidden bg-[#060913] font-sans text-slate-100 selection:bg-amber-200/25">
      {/* deep sky */}
      <div className="pointer-events-none fixed inset-0 bg-[radial-gradient(120%_90%_at_50%_0%,#101a33_0%,#0a1022_45%,#060913_100%)]" />

      {/* slow nebulas */}
      <div className="animate-zen-drift pointer-events-none fixed -left-40 top-1/4 h-[34rem] w-[34rem] rounded-full bg-indigo-500/10 blur-3xl" />
      <div
        className="animate-zen-drift pointer-events-none fixed -right-40 bottom-1/4 h-[30rem] w-[30rem] rounded-full bg-amber-400/[7%] blur-3xl"
        style={{ animationDelay: '-6s' }}
      />

      <Starfield />
      <div className="zen-vignette pointer-events-none fixed inset-0" />

      {/* wordmark */}
      <div className="fixed left-6 top-6 z-20 font-sans text-[10px] uppercase tracking-[0.5em] text-white/30">
        zen relay
      </div>

      {/* the current moment */}
      <main key={chapter} className="relative z-10">
        {chapter === 0 && <Intro onBegin={next} />}
        {chapter === 1 && <KindleKey onComplete={next} />}
        {chapter === 2 && <SignNote onComplete={next} />}
        {chapter === 3 && <ReleaseNote onComplete={next} />}
        {chapter === 4 && <FadingRelay onComplete={next} />}
        {chapter === 5 && <Constellation onComplete={next} />}
        {chapter === 6 && <Farewell onRestart={restart} />}
      </main>

      {inJourney && <ProgressDots total={TOTAL_CHAPTERS} current={chapter} />}

      <a
        href="https://shakespeare.diy"
        target="_blank"
        rel="noreferrer"
        className="fixed bottom-4 right-5 z-20 font-sans text-[10px] uppercase tracking-[0.3em] text-white/20 transition-colors duration-500 hover:text-white/50"
      >
        vibed with shakespeare
      </a>
    </div>
  );
}
